import {
  Alert,
  Badge,
  Button,
  CategoryBrowser,
  Cell,
  fill,
  groupItems,
  hasGroupedProducts,
  itemInCategory,
  LoadMore,
  Loading,
  SelectField,
  Table,
  TextField,
} from '@avrahami/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  masterCategoriesQuery,
  masterProductIndexQuery,
  masterProductsQuery,
  type MasterProductFilters,
} from '../api/platform';
import { he } from '../i18n/he';
import {
  pickedCountText,
  pickState,
  productsByCategory,
  productsInCategories,
  searchProducts,
  setPicked,
  togglePicked,
  type HiddenProduct,
  type HiddenReason,
  type PickedProduct,
  type VisibilityChange,
} from '../lib/exclusions';
import { errorMessage } from '../lib/format';

const BOX_CLASS = 'size-4 rounded border-slate-300 accent-brand-600';
const words = he.exclusions.table;

type View = 'all' | 'shown' | 'hidden';
const VIEWS: readonly View[] = ['all', 'shown', 'hidden'];

/** One row of the table: a catalog product, or one the store does not show. */
interface Row extends PickedProduct {
  model?: string | null;
  /** The catalog's categories the product is placed in, when known. */
  categoryIds?: readonly string[] | undefined;
}

/** A row whose categories are known. */
type PlacedRow = Row & { categoryIds: readonly string[] };

/** What every list of the table needs to draw its rows and act on them. */
interface Rows {
  hiddenIds: ReadonlySet<string>;
  /** Why the store does not show a product, when it is known. */
  reasons: ReadonlyMap<string, HiddenReason>;
  /** Products the store shows by the platform's choice, against the site. */
  chosenIds: ReadonlySet<string>;
  selected: readonly PickedProduct[];
  onSelect: (selected: PickedProduct[]) => void;
  onChange: (change: VisibilityChange) => void;
  canChange: boolean;
  disabled: boolean;
}

/**
 * The catalog's products as one store gets them: a table, as the store's own product list,
 * under the catalog's categories (a search shows its results at once). Every row says whether
 * the product is shown in the store and can change it; rows can also be ticked, the shown and
 * the hidden ones alike, and changed together. A whole category, or everything the view lists,
 * is ticked at once by its own box. The view lists the whole catalog, what the store shows, or
 * what it does not.
 *
 * The products that are not shown and the ticked rows live in the caller, so they survive
 * searches, categories and pages. It holds no form of its own, so it can sit inside one: Enter
 * in the search field searches.
 */
export function StoreProductsTable({
  hidden,
  shownByChoice = [],
  selected,
  onSelect,
  onChange,
  canBrowse = true,
  canChange = true,
  disabled = false,
  name = 'store-products',
}: {
  /** The products the store does not show. */
  hidden: readonly HiddenProduct[];
  /** The products the store shows by the platform's choice, although the site does not. */
  shownByChoice?: readonly PickedProduct[];
  /** The ticked rows. */
  selected: readonly PickedProduct[];
  onSelect: (selected: PickedProduct[]) => void;
  /** Asked for one row (its button) or for the ticked rows it applies to. */
  onChange: (change: VisibilityChange) => void;
  /** The whole catalog can be read (`platform.catalog.read`); otherwise only what is hidden. */
  canBrowse?: boolean;
  canChange?: boolean;
  disabled?: boolean;
  /** Tells the open categories of two tables apart. */
  name?: string;
}) {
  const [search, setSearch] = useState('');
  /** The search the results are shown for; empty shows the categories. */
  const [query, setQuery] = useState('');
  const [chosenView, setView] = useState<View>('all');
  const view: View = canBrowse ? chosenView : 'hidden';
  const categories = useQuery({ ...masterCategoriesQuery, enabled: canBrowse });
  // Every catalog product, short: whole categories are ticked from it, and "shown" lists it.
  const index = useQuery({ ...masterProductIndexQuery, enabled: canBrowse });
  const searching = query.trim() !== '';

  const hiddenIds = new Set(hidden.map((product) => product.id));
  const reasons = new Map(
    hidden.flatMap((product) =>
      product.reason === undefined ? [] : [[product.id, product.reason] as const],
    ),
  );
  const chosenIds = new Set(shownByChoice.map((product) => product.id));
  const ticked = new Set(selected.map((product) => product.id));
  const knownIds = new Set(categories.data?.items.map((category) => category.id) ?? []);
  const rows: Rows = {
    hiddenIds,
    reasons,
    chosenIds,
    selected,
    onSelect,
    onChange,
    canChange,
    disabled,
  };
  const toShow = selected.filter((product) => hiddenIds.has(product.id));
  const toHide = selected.filter((product) => !hiddenIds.has(product.id));

  const catalogProducts = index.data?.items;
  const placedIn = new Map(catalogProducts?.map((product) => [product.id, product.categoryIds]));
  // A store still being set up does not know where its hidden products are: the catalog does.
  const hiddenRows: Row[] = hidden.map((product) => ({
    ...product,
    categoryIds: product.categoryIds ?? placedIn.get(product.id),
  }));
  /** The products the view lists, before a search; undefined while they are not known. */
  const viewProducts: readonly Row[] | undefined =
    view === 'hidden'
      ? hiddenRows
      : view === 'shown'
        ? catalogProducts?.filter((product) => !hiddenIds.has(product.id))
        : catalogProducts;
  // The API searches the catalog (barcodes too); the other views are searched here.
  const listed =
    viewProducts === undefined || (view === 'all' && searching)
      ? undefined
      : searchProducts(viewProducts, query);

  const selectViewText = fill(words.selectView, { count: listed?.length ?? 0 });
  const pickBox = (products: readonly PickedProduct[], label: string, testId: string) => (
    <PickBox
      products={products}
      ticked={ticked}
      label={label}
      testId={testId}
      disabled={disabled}
      onPick={(on) => {
        onSelect(setPicked(selected, products.map(picked), on));
      }}
    />
  );
  /** The box of a category row: every product of the list under it. */
  const categoryPick = (pool: readonly PlacedRow[]) => {
    if (!canChange) return undefined;
    const byCategory = productsByCategory(pool, knownIds);
    return (categoryIds: readonly string[], categoryName: string) =>
      pickBox(
        productsInCategories(byCategory, categoryIds),
        `${words.selectCategory}: ${categoryName}`,
        'store-products-select-category',
      );
  };

  const catalog = (filters: MasterProductFilters) => <CatalogRows filters={filters} rows={rows} />;
  const empty = (
    <p className="text-sm text-slate-500" data-testid="store-products-empty">
      {words.empty}
    </p>
  );

  /** A list in hand (what the store shows, or what it does not), under the categories. */
  const listInHand = (found: readonly Row[]) => {
    if (found.length === 0) return empty;
    const placed = found.flatMap((product) =>
      product.categoryIds === undefined ? [] : [{ ...product, categoryIds: product.categoryIds }],
    );
    // A search, or products whose categories are not known, show as one list.
    if (searching || categories.data === undefined || placed.length < found.length) {
      return <ProductRows items={found} rows={rows} />;
    }
    return (
      <CategoryBrowser
        name={`${name}:${view}`}
        groups={groupItems(categories.data.items, placed)}
        renderPick={categoryPick(placed)}
        renderProducts={(categoryId) => (
          <ProductRows
            items={placed.filter((product) => itemInCategory(product, categoryId, knownIds))}
            rows={rows}
          />
        )}
      />
    );
  };

  return (
    <div
      role="group"
      aria-label={words.label}
      className="flex flex-col gap-4"
      data-testid="store-products"
    >
      <div className="flex flex-wrap items-end gap-3">
        <TextField
          label={words.search}
          name="storeProductSearch"
          type="search"
          className="w-full sm:w-72"
          maxLength={100}
          data-testid="store-products-search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // Inside a form, Enter would submit the form around the table.
            event.preventDefault();
            setQuery(search);
          }}
        />
        {canBrowse ? (
          <SelectField
            label={words.view}
            name="storeProductView"
            data-testid="store-products-view"
            value={view}
            onChange={(event) => {
              setView(VIEWS.find((option) => option === event.target.value) ?? 'all');
            }}
          >
            <option value="all">{words.viewAll}</option>
            <option value="shown">{words.viewShown}</option>
            <option value="hidden">{words.viewHidden}</option>
          </SelectField>
        ) : null}
        <Button
          tone="secondary"
          onClick={() => {
            setQuery(search);
          }}
          data-testid="store-products-search-submit"
        >
          {he.common.search}
        </Button>
      </div>

      {canChange ? (
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md bg-slate-50 p-3"
          data-testid="store-products-bulk"
        >
          {listed === undefined ? null : (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              {pickBox(listed, selectViewText, 'store-products-select-view')}
              {selectViewText}
            </label>
          )}
          <p
            className="me-auto text-sm font-medium text-slate-700"
            data-testid="store-products-selected"
          >
            {pickedCountText(selected.length)}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              tone="secondary"
              data-testid="store-products-show-selected"
              disabled={disabled || toShow.length === 0}
              onClick={() => {
                onChange({ products: toShow, shown: true });
              }}
            >
              {fill(words.showSelected, { count: toShow.length })}
            </Button>
            <Button
              tone="secondary"
              data-testid="store-products-hide-selected"
              disabled={disabled || toHide.length === 0}
              onClick={() => {
                onChange({ products: toHide, shown: false });
              }}
            >
              {fill(words.hideSelected, { count: toHide.length })}
            </Button>
            <Button
              tone="secondary"
              disabled={disabled || selected.length === 0}
              onClick={() => {
                onSelect([]);
              }}
            >
              {words.clear}
            </Button>
          </div>
        </div>
      ) : null}

      {view === 'hidden' ? (
        listInHand(searchProducts(hiddenRows, query))
      ) : view === 'shown' ? (
        <>
          {index.isPending ? <Loading /> : null}
          {index.isError ? <Alert>{errorMessage(index.error)}</Alert> : null}
          {listed === undefined ? null : listInHand(listed)}
        </>
      ) : searching ? (
        catalog({ q: query, categoryId: '' })
      ) : (
        <>
          {categories.isPending ? <Loading /> : null}
          {categories.isError ? <Alert>{errorMessage(categories.error)}</Alert> : null}
          {categories.data === undefined ? null : hasGroupedProducts(categories.data) ? (
            <CategoryBrowser
              name={name}
              groups={categories.data}
              renderPick={catalogProducts === undefined ? undefined : categoryPick(catalogProducts)}
              renderProducts={(categoryId) => catalog({ q: '', categoryId })}
            />
          ) : (
            empty
          )}
        </>
      )}
    </div>
  );
}

/**
 * A box that ticks every product of a list at once: ticked when all of them are, half-ticked
 * when only some are.
 */
function PickBox({
  products,
  ticked,
  label,
  testId,
  disabled,
  onPick,
}: {
  products: readonly PickedProduct[];
  ticked: ReadonlySet<string>;
  label: string;
  testId: string;
  disabled: boolean;
  onPick: (on: boolean) => void;
}) {
  const state = pickState(products, ticked);
  return (
    <input
      type="checkbox"
      aria-label={label}
      title={label}
      data-testid={testId}
      data-state={state}
      ref={(box) => {
        if (box !== null) box.indeterminate = state === 'some';
      }}
      checked={state === 'all'}
      disabled={disabled || products.length === 0}
      onChange={() => {
        onPick(state !== 'all');
      }}
      className={BOX_CLASS}
    />
  );
}

/** The catalog products of one category, or the results of a search, with "load more". */
function CatalogRows({ filters, rows }: { filters: MasterProductFilters; rows: Rows }) {
  const list = useInfiniteQuery(masterProductsQuery(filters));
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <>
      {list.isPending ? <Loading /> : null}
      {list.isError ? <Alert>{errorMessage(list.error)}</Alert> : null}
      {list.isSuccess && items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="store-products-empty">
          {words.empty}
        </p>
      ) : null}
      <ProductRows
        items={items}
        rows={rows}
        loadAll={async () => {
          let pages = list.data?.pages ?? [];
          let more = list.hasNextPage;
          while (more) {
            const next = await list.fetchNextPage();
            if (next.data === undefined || next.isError) break;
            pages = next.data.pages;
            more = next.hasNextPage;
          }
          return pages.flatMap((page) => page.items);
        }}
      />
      <LoadMore
        hasMore={list.hasNextPage}
        busy={list.isFetchingNextPage}
        onMore={() => {
          void list.fetchNextPage();
        }}
      />
    </>
  );
}

/**
 * The table itself. The checkbox of its heading ticks every product of this list, whether it is
 * shown in the store or not; a list that has more pages loads them first (`loadAll`).
 */
function ProductRows({
  items,
  rows,
  loadAll,
}: {
  items: readonly Row[];
  rows: Rows;
  /** Every product of the list, when the rows are only its first pages. */
  loadAll?: () => Promise<readonly Row[]>;
}) {
  const { hiddenIds, reasons, chosenIds, selected, onSelect, onChange, canChange, disabled } = rows;
  const [loadingAll, setLoadingAll] = useState(false);
  if (items.length === 0) return null;
  const ticked = new Set(selected.map((product) => product.id));
  const head = [
    words.name,
    words.sku,
    words.status,
    ...(canChange ? [words.actions] : []),
  ] as const;
  return (
    <Table
      head={head}
      lead={
        canChange ? (
          <input
            type="checkbox"
            aria-label={words.selectAll}
            title={words.selectAll}
            data-testid="store-products-select-all"
            // Ticked at once, while the rest of the list is still being loaded.
            checked={loadingAll || items.every((product) => ticked.has(product.id))}
            disabled={disabled || loadingAll}
            onChange={(event) => {
              const on = event.target.checked;
              if (!on || loadAll === undefined) {
                onSelect(setPicked(selected, items.map(picked), on));
                return;
              }
              setLoadingAll(true);
              void loadAll()
                .then((all) => {
                  onSelect(setPicked(selected, all.map(picked), true));
                })
                .finally(() => {
                  setLoadingAll(false);
                });
            }}
            className={BOX_CLASS}
          />
        ) : undefined
      }
    >
      {items.map((product) => {
        const shown = !hiddenIds.has(product.id);
        const reason = shown ? undefined : reasons.get(product.id);
        const byChoice = shown && chosenIds.has(product.id);
        return (
          <tr
            key={product.id}
            data-testid="store-product-row"
            data-sku={product.sku}
            data-shown={String(shown)}
            data-reason={reason ?? (byChoice ? 'CHOSEN' : '')}
          >
            {canChange ? (
              <Cell>
                <input
                  type="checkbox"
                  aria-label={`${words.select}: ${product.name}`}
                  data-testid="store-product-select"
                  checked={ticked.has(product.id)}
                  disabled={disabled || loadingAll}
                  onChange={() => {
                    onSelect(togglePicked(selected, picked(product)));
                  }}
                  className={`mt-0.5 ${BOX_CLASS}`}
                />
              </Cell>
            ) : null}
            <Cell>
              <span className="font-medium break-words text-slate-900">{product.name}</span>
              {product.model === undefined || product.model === null ? null : (
                <div className="text-xs text-slate-500">
                  {words.model}: <span className="ltr">{product.model}</span>
                </div>
              )}
            </Cell>
            <Cell className="ltr whitespace-nowrap">{product.sku}</Cell>
            <Cell>
              <span
                className="flex flex-wrap gap-1 whitespace-nowrap"
                data-testid="store-product-status"
              >
                <Badge tone={shown ? 'green' : 'slate'}>{shown ? words.shown : words.hidden}</Badge>
                {reason === undefined ? null : (
                  <span
                    title={
                      reason === 'SITE'
                        ? he.exclusions.reasonSiteHelp
                        : he.exclusions.reasonPlatformHelp
                    }
                  >
                    <Badge tone={reason === 'SITE' ? 'slate' : 'amber'}>
                      {reason === 'SITE' ? he.exclusions.reasonSite : he.exclusions.reasonPlatform}
                    </Badge>
                  </span>
                )}
                {byChoice ? (
                  <span title={he.exclusions.shownByChoiceHelp}>
                    <Badge tone="blue">{he.exclusions.shownByChoice}</Badge>
                  </span>
                ) : null}
              </span>
            </Cell>
            {canChange ? (
              <Cell>
                <Button
                  tone="secondary"
                  aria-label={`${shown ? words.hide : words.show}: ${product.name}`}
                  data-testid="store-product-toggle"
                  disabled={disabled}
                  onClick={() => {
                    onChange({ products: [picked(product)], shown: !shown });
                  }}
                >
                  {shown ? words.hide : words.show}
                </Button>
              </Cell>
            ) : null}
          </tr>
        );
      })}
    </Table>
  );
}

/** What is kept of a ticked row. */
function picked({ id, name, sku }: Row): PickedProduct {
  return { id, name, sku };
}
